/*
 * AMASS × USBG member perk form (sections/amass-usbg-perk.liquid)
 * ----------------------------------------------------------------------------
 * On submit:
 *   1. Validates client-side — required fields + email format — with inline
 *      errors. The address is deliberately under-validated (a messy address
 *      beats an abandoned form): non-empty is enough, no ZIP regex.
 *   2. Records the lead in Klaviyo with the PUBLIC client APIs (company id from
 *      the theme's Klaviyo setting; no private key in the browser):
 *        a. /client/subscriptions → adds the profile to the USBG Members list
 *           (single opt-in) with the bar name / role / address as profile
 *           properties + location. This is the distributor export.
 *        b. /client/events → logs a "USBG Code Requested" event. The Klaviyo
 *           flow that emails the unique 15% code triggers on THIS metric, so a
 *           member who lost the email can re-submit and get the same code
 *           re-sent (a list-join trigger would only fire once per profile).
 *   3. Shows an inline success state that replaces the form.
 *
 * The event call is the one that matters (it sends the code); the list call is
 * best-effort. If both fail the form shows a retry message and re-enables.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_REVISION = '2024-10-15';

  function labelFor(input) {
    var wrap = input.closest('.usbg-field');
    var label = wrap && wrap.querySelector('.usbg-label');
    return (label ? label.textContent : (input.name || 'This field')).replace(/\*/g, '').trim();
  }

  function setError(form, input, message) {
    input.setAttribute('aria-invalid', 'true');
    var el = form.querySelector('[data-usbg-error-for="' + input.name + '"]');
    if (el) { el.textContent = message; el.hidden = false; }
  }

  function clearError(form, input) {
    input.removeAttribute('aria-invalid');
    var el = form.querySelector('[data-usbg-error-for="' + input.name + '"]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  function validate(form) {
    var first = null;
    form.querySelectorAll('[data-usbg-required]').forEach(function (input) {
      clearError(form, input);
      if (!(input.value || '').trim()) {
        setError(form, input, labelFor(input) + ' is required.');
        if (!first) first = input;
      }
    });
    var email = form.querySelector('[data-usbg-email]');
    if (email) {
      var v = (email.value || '').trim();
      if (v && !EMAIL_RE.test(v)) {
        setError(form, email, 'Enter a valid email address.');
        if (!first) first = email;
      }
    }
    // Single-select question(s): the fieldset is required as a group.
    form.querySelectorAll('[data-usbg-radio-required]').forEach(function (set) {
      var radios = set.querySelectorAll('input[type="radio"]');
      var name = radios.length ? radios[0].name : '';
      var err = form.querySelector('[data-usbg-error-for="' + name + '"]');
      var any = set.querySelector('input[type="radio"]:checked');
      if (!any) {
        set.setAttribute('aria-invalid', 'true');
        if (err) { err.textContent = 'Pick one.'; err.hidden = false; }
        if (!first && radios.length) first = radios[0];
      } else {
        set.removeAttribute('aria-invalid');
        if (err) { err.textContent = ''; err.hidden = true; }
      }
    });
    if (first) { try { first.focus(); } catch (e) { /* noop */ } }
    return !first;
  }

  function collect(form) {
    var data = {};
    form.querySelectorAll('input, select').forEach(function (input) {
      if (!input.name) return;
      if (input.type === 'radio') {
        if (input.checked) data[input.name] = (input.value || '').trim();
        else if (!(input.name in data)) data[input.name] = '';
        return;
      }
      data[input.name] = (input.value || '').trim();
    });
    return data;
  }

  function buildProfile(data, source) {
    var address = [data.street, data.city, [data.state, data.zip].filter(Boolean).join(' ')]
      .filter(Boolean).join(', ');
    return {
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      location: {
        address1: data.street || undefined,
        city: data.city || undefined,
        region: data.state || undefined,
        zip: data.zip || undefined,
        country: 'United States'
      },
      properties: {
        'USBG Member': true,
        'Bar Name': data.bar_name,
        'Bar Address': address,
        'Bar Street': data.street,
        'Bar City': data.city,
        'Bar State': data.state,
        'Bar ZIP': data.zip,
        'Role': data.role,
        'Skinny Menu Depth': data.skinny_menu || '',
        'USBG Signup Source': source
      }
    };
  }

  function klaviyoPost(path, company, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(payload)
    }).then(function (res) { return res.ok; }).catch(function () { return false; });
  }

  function subscribe(company, list, profile, source) {
    if (!list) return Promise.resolve(false);
    return klaviyoPost('subscriptions', company, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: profile } }
        },
        relationships: { list: { data: { type: 'list', id: list } } }
      }
    });
  }

  function track(company, metric, profile, data, source) {
    return klaviyoPost('events', company, {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: profile } },
          properties: {
            'Bar Name': data.bar_name,
            'Bar Address': profile.properties['Bar Address'],
            'Role': data.role,
            'Skinny Menu Depth': data.skinny_menu || '',
            'Source': source,
            'Page': window.location.href
          }
        }
      }
    });
  }

  function onsiteIdentify(profile) {
    // Bonus: tie the Klaviyo onsite cookie to this profile so later browsing on
    // amass.com is attributed. Never blocks the submit.
    try {
      var kl = window.klaviyo || window._learnq;
      if (kl && typeof kl.push === 'function') {
        kl.push(['identify', { $email: profile.email, $first_name: profile.first_name, $last_name: profile.last_name }]);
      }
    } catch (e) { /* noop */ }
  }

  function setBusy(form, busy) {
    var btn = form.querySelector('[data-usbg-submit]');
    if (!btn) return;
    btn.disabled = busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) { btn.dataset.label = btn.textContent; btn.textContent = 'Sending…'; }
    else if (btn.dataset.label) { btn.textContent = btn.dataset.label; }
  }

  function showStatus(form, message, isError) {
    var status = form.querySelector('[data-usbg-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', !!isError);
    status.hidden = !message;
  }

  function succeed(root, form, email) {
    var success = root.querySelector('[data-usbg-success]');
    root.classList.add('is-submitted');
    if (success) {
      success.querySelectorAll('[data-usbg-email-out]').forEach(function (el) { el.textContent = email; });
      success.hidden = false;
      try { success.setAttribute('tabindex', '-1'); success.focus(); } catch (e) { /* noop */ }
    }
    form.hidden = true;
    try { root.dispatchEvent(new CustomEvent('usbg:submitted')); } catch (e) { /* noop */ }
    try {
      if (window.dataLayer) window.dataLayer.push({ event: 'usbg_code_requested' });
    } catch (e) { /* noop */ }
  }

  // Mobile sticky CTA: shown only while the form card is off screen (product
  // details come first on a phone), hidden again once the form is submitted.
  function bindSticky(root) {
    var sticky = root.querySelector('[data-usbg-sticky]');
    var card = root.querySelector('#code');
    if (!sticky || !card || sticky.__usbgBound) return;
    sticky.__usbgBound = true;
    root.classList.add('has-sticky');
    var cardVisible = false;
    function update() {
      sticky.hidden = cardVisible || root.classList.contains('is-submitted');
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        cardVisible = entries[0].isIntersecting;
        update();
      }, { threshold: 0.15 }).observe(card);
    } else {
      sticky.hidden = true;
    }
    root.addEventListener('usbg:submitted', update);
  }

  function bind(root) {
    bindSticky(root);
    var form = root.querySelector('[data-usbg-form]');
    if (!form || form.__usbgBound) return;
    form.__usbgBound = true;

    var company = root.getAttribute('data-klaviyo-company') || '';
    var list = root.getAttribute('data-klaviyo-list') || '';
    var metric = root.getAttribute('data-klaviyo-event') || 'USBG Code Requested';
    var source = root.getAttribute('data-klaviyo-source') || 'USBG member perk page';

    form.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.getAttribute && t.getAttribute('aria-invalid') === 'true') clearError(form, t);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      showStatus(form, '', false);
      if (!validate(form)) return;

      var data = collect(form);
      // Honeypot: bots fill every field; humans never see this one.
      if (data.website) { succeed(root, form, data.email); return; }
      if (!company) {
        showStatus(form, 'This form isn’t connected yet. Email us and we’ll send your code by hand.', true);
        return;
      }

      var profile = buildProfile(data, source);
      setBusy(form, true);

      Promise.all([
        track(company, metric, profile, data, source),
        subscribe(company, list, profile, source)
      ]).then(function (results) {
        var tracked = results[0];
        var listed = results[1];
        if (tracked || listed) {
          onsiteIdentify(profile);
          succeed(root, form, data.email);
        } else {
          setBusy(form, false);
          showStatus(form, 'Something went wrong sending your details. Please try again in a moment.', true);
        }
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-usbg-root]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
})();
